import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * How long a stop waits for a microphone that is still opening. Past this the mic is
 * taken to have failed to open (permission refused, device busy) and react-mic is let
 * go, so the learner's next press asks for the microphone again instead of waiting on
 * a request that will never answer.
 */
export const MIC_OPEN_GRACE_MS = 5000;

/**
 * Keeps react-mic recording until its microphone has actually opened.
 *
 * react-mic opens the mic asynchronously (getUserMedia, then AudioContext.resume) and
 * silently ignores a stop that arrives before its MediaRecorder is running. The recorder
 * then starts anyway with nothing left to stop it: it records on until the next stop,
 * and that turn's audio carries everything the mic heard in between — Madame AI's own
 * reply included, so she answers herself instead of the learner. A quick double-click
 * on the record button was enough to get there.
 *
 * So react-mic's `record` prop is held on from the moment a recording starts until the
 * recorder delivers its first chunk of audio. A stop that lands before then still ends
 * the turn in the UI at once; react-mic is closed the moment it has something to close.
 *
 * @param {boolean} record - Whether the learner is recording (the button's state).
 * @returns {{
 *   micRecord: boolean,
 *   markOpening: () => void,
 *   markStopped: () => boolean,
 *   handleMicData: () => void,
 * }} micRecord feeds react-mic's `record` prop; handleMicData is its `onData`.
 */
export default function useMicOpenGate(record) {
  const [opening, setOpening] = useState(false);
  const openingRef = useRef(false);
  const graceTimerRef = useRef(null);

  const clearGraceTimer = useCallback(() => {
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }
  }, []);

  const setMicOpening = useCallback((value) => {
    openingRef.current = value;
    setOpening(value);
  }, []);

  useEffect(() => clearGraceTimer, [clearGraceTimer]);

  /** A recording is starting: react-mic is about to ask for the microphone. */
  const markOpening = useCallback(() => {
    clearGraceTimer();
    setMicOpening(true);
  }, [clearGraceTimer, setMicOpening]);

  /**
   * The learner stopped. Returns whether the mic had opened — if it had not, nothing of
   * this turn was captured, and react-mic stays on until it opens (or the grace runs out).
   */
  const markStopped = useCallback(() => {
    if (!openingRef.current) return true;

    clearGraceTimer();
    graceTimerRef.current = setTimeout(() => {
      graceTimerRef.current = null;
      setMicOpening(false);
    }, MIC_OPEN_GRACE_MS);
    return false;
  }, [clearGraceTimer, setMicOpening]);

  /** react-mic binds its callbacks once, at mount — so this reads refs only. */
  const handleMicData = useCallback(() => {
    if (!openingRef.current) return;
    clearGraceTimer();
    setMicOpening(false);
  }, [clearGraceTimer, setMicOpening]);

  return {
    micRecord: record || opening,
    markOpening,
    markStopped,
    handleMicData,
  };
}
